import { Music, Plane, Coffee, Pizza, Star, Heart, Award, Trophy, Sparkles } from "lucide-react";

export type QAItem = { icon: string; question: string; answer: string };
export type BookingCTA = { label: string; url: string };

export const QA_ICONS = [
  { key: "music", label: "Music", Icon: Music },
  { key: "plane", label: "Plane", Icon: Plane },
  { key: "coffee", label: "Coffee", Icon: Coffee },
  { key: "pizza", label: "Pizza", Icon: Pizza },
  { key: "star", label: "Star", Icon: Star },
  { key: "heart", label: "Heart", Icon: Heart },
  { key: "award", label: "Award", Icon: Award },
  { key: "trophy", label: "Trophy", Icon: Trophy },
  { key: "sparkles", label: "Sparkles", Icon: Sparkles },
];

export const iconFor = (key: string) => QA_ICONS.find((i) => i.key === key)?.Icon ?? Star;

const inp = "w-full px-3 py-2 rounded-xl bg-muted border border-border focus:outline-none focus:ring-2 focus:ring-primary text-sm";

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <label className="text-xs font-semibold uppercase tracking-wider">{label}</label>
    <div className="mt-1">{children}</div>
  </div>
);

type Details = {
  jersey_number?: string | null;
  position_short?: string | null;
  position_long?: string | null;
  height?: string | null;
  hometown?: string | null;
  college?: string | null;
  occupation?: string | null;
  years_playing?: number | null;
  throws_hand?: string | null;
  high_school?: string | null;
  mantra_quote?: string | null;
  support_blurb?: string | null;
  booking_inquiry_email?: string | null;
  qa?: QAItem[] | null;
  booking_ctas?: BookingCTA[] | null;
};

export const ProfileDetailsEditor = <T extends Details>({
  value,
  onChange,
}: {
  value: T;
  onChange: (patch: Partial<T>) => void;
}) => {
  const qa = value.qa ?? [];
  const ctas = value.booking_ctas ?? [];

  return (
    <div className="space-y-5">
      <div className="rounded-xl bg-muted/40 border border-border p-4 space-y-3">
        <p className="text-xs font-semibold uppercase tracking-wider">Profile details (optional)</p>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Jersey #">
            <input className={inp} value={value.jersey_number ?? ""} onChange={(e) => onChange({ jersey_number: e.target.value } as Partial<T>)} maxLength={10} />
          </Field>
          <Field label="Position (short)">
            <input className={inp} value={value.position_short ?? ""} onChange={(e) => onChange({ position_short: e.target.value } as Partial<T>)} maxLength={10} placeholder="DB" />
          </Field>
          <Field label="Position (long)">
            <input className={inp} value={value.position_long ?? ""} onChange={(e) => onChange({ position_long: e.target.value } as Partial<T>)} maxLength={60} placeholder="Defensive Back" />
          </Field>
          <Field label="Height">
            <input className={inp} value={value.height ?? ""} onChange={(e) => onChange({ height: e.target.value } as Partial<T>)} maxLength={20} placeholder={`5'5"`} />
          </Field>
          <Field label="Hometown">
            <input className={inp} value={value.hometown ?? ""} onChange={(e) => onChange({ hometown: e.target.value } as Partial<T>)} maxLength={80} />
          </Field>
          <Field label="College">
            <input className={inp} value={value.college ?? ""} onChange={(e) => onChange({ college: e.target.value } as Partial<T>)} maxLength={80} />
          </Field>
          <Field label="Occupation">
            <input className={inp} value={value.occupation ?? ""} onChange={(e) => onChange({ occupation: e.target.value } as Partial<T>)} maxLength={80} />
          </Field>
          <Field label="Years Playing">
            <input type="number" className={inp} value={value.years_playing ?? ""} onChange={(e) => onChange({ years_playing: e.target.value ? Number(e.target.value) : null } as Partial<T>)} />
          </Field>
          <Field label="Throws">
            <input className={inp} value={value.throws_hand ?? ""} onChange={(e) => onChange({ throws_hand: e.target.value } as Partial<T>)} maxLength={20} placeholder="Right / Left" />
          </Field>
          <Field label="High School">
            <input className={inp} value={value.high_school ?? ""} onChange={(e) => onChange({ high_school: e.target.value } as Partial<T>)} maxLength={80} />
          </Field>
        </div>
      </div>

      <Field label="Mantra / Quote">
        <textarea
          className={inp}
          rows={3}
          value={value.mantra_quote ?? ""}
          onChange={(e) => onChange({ mantra_quote: e.target.value } as Partial<T>)}
          maxLength={220}
          placeholder="Football taught me confidence before it taught me how to tackle."
        />
        <p className="text-[11px] text-muted-foreground mt-1">
          Appears on public profile as… the full-width “Her Mantra” quote. Hidden when empty or identical to the Tagline.
        </p>
      </Field>

      <Field label="Support section copy">
        <textarea
          className={inp}
          rows={3}
          value={value.support_blurb ?? ""}
          onChange={(e) => onChange({ support_blurb: e.target.value } as Partial<T>)}
          maxLength={400}
          placeholder="Be part of her journey. Your support helps this athlete continue putting resources into the training, travel, equipment and support required to compete and grow her career."
        />
        <p className="text-[11px] text-muted-foreground mt-1">Shown under the “Back …” headline. Leave blank to use the default copy.</p>
      </Field>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wider">Get to know me — Q&amp;A (max 8)</label>
        <p className="text-[11px] text-muted-foreground mt-1">Appears on public profile as… the “Get to Know Her” section.</p>
        <div className="space-y-2 mt-2">
          {qa.map((q, i) => (
            <div key={i} className="rounded-xl bg-muted/40 border border-border p-3 space-y-2">
              <div className="flex gap-2">
                <select
                  className={inp + " max-w-[8rem]"}
                  value={q.icon}
                  onChange={(e) => {
                    const next = [...qa];
                    next[i] = { ...q, icon: e.target.value };
                    onChange({ qa: next } as Partial<T>);
                  }}
                >
                  {QA_ICONS.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
                </select>
                <input
                  className={inp}
                  value={q.question}
                  onChange={(e) => {
                    const next = [...qa];
                    next[i] = { ...q, question: e.target.value };
                    onChange({ qa: next } as Partial<T>);
                  }}
                  placeholder="Favorite Pregame Song?"
                  maxLength={80}
                />
                <button type="button" onClick={() => onChange({ qa: qa.filter((_, j) => j !== i) } as Partial<T>)} className="px-3 text-destructive">✕</button>
              </div>
              <input
                className={inp}
                value={q.answer}
                onChange={(e) => {
                  const next = [...qa];
                  next[i] = { ...q, answer: e.target.value };
                  onChange({ qa: next } as Partial<T>);
                }}
                placeholder="Answer"
                maxLength={120}
              />
            </div>
          ))}
          {qa.length < 8 && (
            <button
              type="button"
              onClick={() => onChange({ qa: [...qa, { icon: "star", question: "", answer: "" }] } as Partial<T>)}
              className="text-sm text-primary font-semibold"
            >
              + Add Q&amp;A
            </button>
          )}
        </div>
      </div>

      <div>
        <label className="text-xs font-semibold uppercase tracking-wider">Work-with-me buttons (max 6)</label>
        <p className="text-[11px] text-muted-foreground mt-1">Appears on public profile as… buttons in the “Work With [Name]” section. The inquiry email is never shown publicly.</p>
        <div className="space-y-2 mt-2">
          {ctas.map((c, i) => (
            <div key={i} className="flex gap-2">
              <input
                className={inp}
                value={c.label}
                onChange={(e) => {
                  const next = [...ctas];
                  next[i] = { ...c, label: e.target.value };
                  onChange({ booking_ctas: next } as Partial<T>);
                }}
                placeholder="Book Her to Speak"
                maxLength={40}
              />
              <input
                className={inp}
                value={c.url}
                onChange={(e) => {
                  const next = [...ctas];
                  next[i] = { ...c, url: e.target.value };
                  onChange({ booking_ctas: next } as Partial<T>);
                }}
                placeholder="https:// or mailto:"
              />
              <button type="button" onClick={() => onChange({ booking_ctas: ctas.filter((_, j) => j !== i) } as Partial<T>)} className="px-3 text-destructive">✕</button>
            </div>
          ))}
          {ctas.length < 6 && (
            <button
              type="button"
              onClick={() => onChange({ booking_ctas: [...ctas, { label: "", url: "" }] } as Partial<T>)}
              className="text-sm text-primary font-semibold"
            >
              + Add button
            </button>
          )}
        </div>
        <Field label="Inquiry email (used for 'Inquire Now' when no CTA URL is set)">
          <input className={inp} value={value.booking_inquiry_email ?? ""} onChange={(e) => onChange({ booking_inquiry_email: e.target.value } as Partial<T>)} placeholder="booking@..." />
        </Field>
      </div>
    </div>
  );
};
